import { useCallback, useContext, useState } from 'react';
import EDBox from '../../../../../../BaseComponents/EDBox/EDBox';
import { EDAutoCompleteEdit } from '../../../../../../BaseComponents/AutoCompleteEdit/EDAutoCompleteEdit';
import EDTypography from '../../../../../../BaseComponents/Typography/EDTypography';
import { IBusinessDataFormContext } from '../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import { NEWLY_ADDED_ID_PREFIX_FOR_UPDATE } from '../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Const/BusinessDataFormConst';
import shortid from 'shortid';
import { useQuery } from '@apollo/client';

import { ActiveDatabaseContext } from '../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import {
  BOPsalmerAllQuery,
  BOPsalmerAllQueryVariables,
} from '../Queries/_QueryTypes/BOPsalmerAllQuery';
import psalmerQuery from '../Queries/BOPsalmerAllQuery.gql';
import { TEDBOAktivitetTypEnum } from '../../../../../../globalQueryTypes';
import { StatistikrapporteringQuery_node_TEDBOAktivitet__Associations_AktPsalmer_All } from '../../Statistikrapportering/Queries/_QueryTypes/StatistikrapporteringQuery';
import { repeat } from 'ramda';
import { useIsLargeScreen } from '../../../../../../Hooks/useFullscreenView';
import { ActiveRoutineContext } from '../../../../../Routing/ActiveRoutine/ActiveRoutineContext';
import EDButton from '../../../../../../BaseComponents/Buttons/Button/EDButton';
import EDIcon from '../../../../../../BaseComponents/Icon/EDIcon';

const HARD_LIMIT = 10;

interface IProps {
  formContext: IBusinessDataFormContext<any, unknown>;
  activityType: TEDBOAktivitetTypEnum;
  isBegravningAndIsSvKyrkanBegrType: boolean;
  isNotWithinValidDateRange: boolean;
}

const Psalmer = (props: IProps) => {
  const { activeDatabase } = useContext(ActiveDatabaseContext);
  let aktPsalmerAllFormContext =
    props.formContext?.formState?.currentValues?._Associations?.AktPsalmer_All;
  const isLargeScreen = useIsLargeScreen();
  const routineAvailabilityContext = useContext(ActiveRoutineContext);
  const routineIDBenamning = routineAvailabilityContext?.rutinInfo.benamning;

  const [extraRows, setExtraRows] = useState(0);
  // Tracks saved rows visually cleared but not yet deleted. We defer the
  // Delete_Values until blur so that if the user immediately re-selects a psalm
  // we can use Update_Values instead — avoiding a duplicate-key DB error.
  const [locallyCleared, setLocallyCleared] = useState<Record<string, boolean>>(
    {}
  );

  const softLimit =
    props.activityType === 'GUDSTJANST'
      ? 5
      : props.activityType === 'VIGSEL'
      ? 3
      : props.activityType === 'DOP'
      ? 3
      : props.isBegravningAndIsSvKyrkanBegrType
      ? 3
      : 0;

  const { data: psalmerAll } = useQuery<
    BOPsalmerAllQuery,
    BOPsalmerAllQueryVariables
  >(psalmerQuery, {
    variables: {
      dbname: activeDatabase || '',
    },
  });

  const updateValue = useCallback(
    (itemId: string | undefined, option: any, isExtraRow: boolean) => {
      const isSavedRow =
        itemId !== undefined &&
        !itemId.includes(NEWLY_ADDED_ID_PREFIX_FOR_UPDATE);

      if (option === null) {
        if (itemId) {
          if (isSavedRow) {
            // Defer the delete: mark locally cleared so the row renders empty,
            // but don't write to the form context yet. If the user picks a new
            // psalm before blurring we'll reuse this ID with Update_Values,
            // avoiding a duplicate-key DB error on re-selecting the same psalm.
            setLocallyCleared(prev => ({ ...prev, [itemId]: true }));
          } else {
            // Unsaved row (NEWLY_ADDED prefix) — safe to delete immediately.
            props.formContext?.update([
              {
                operation: 'Delete_Values',
                associationName: 'AktPsalmer',
                id: itemId,
              },
            ]);
          }
          const contextCountAfterDelete = aktPsalmerAllFormContext.length - 1;
          if (contextCountAfterDelete + extraRows > softLimit) {
            setExtraRows(prev => Math.max(0, prev - 1));
          }
        } else if (isExtraRow) {
          setExtraRows(prev => Math.max(0, prev - 1));
        }
        return;
      }

      // If this row was locally cleared, reuse the saved ID with Update_Values.
      // No delete was ever sent, so no duplicate-key risk.
      if (itemId && locallyCleared[itemId]) {
        setLocallyCleared(prev => {
          const next = { ...prev };
          delete next[itemId];
          return next;
        });
        props.formContext?.update([
          {
            operation: 'Update_Values',
            associationName: 'AktPsalmer',
            id: itemId,
          },
          {
            operation: 'Ref',
            associationName: 'Psalm',
            id: option?.value,
            values: { NRTitel: option?.name },
          },
        ]);
        return;
      }

      const isNewRow =
        itemId?.includes(NEWLY_ADDED_ID_PREFIX_FOR_UPDATE) ||
        itemId === undefined;
      props.formContext?.update([
        {
          operation: isNewRow ? 'Add_Values' : 'Update_Values',
          associationName: 'AktPsalmer',
          id:
            itemId === undefined
              ? `${NEWLY_ADDED_ID_PREFIX_FOR_UPDATE}${shortid.generate()}`
              : itemId,
        },
        {
          operation: 'Ref',
          associationName: 'Psalm',
          id: option?.value,
          values: { NRTitel: option?.name },
        },
      ]);
    },
    [
      props.formContext,
      aktPsalmerAllFormContext,
      extraRows,
      softLimit,
      locallyCleared,
    ]
  );

  // When a locally-cleared saved row loses focus while still empty, commit the
  // deferred Delete_Values so the record is actually removed from the DB.
  const commitDeferredDelete = useCallback(
    (itemId: string) => {
      if (!locallyCleared[itemId]) return;
      setLocallyCleared(prev => {
        const next = { ...prev };
        delete next[itemId];
        return next;
      });
      props.formContext?.update([
        {
          operation: 'Delete_Values',
          associationName: 'AktPsalmer',
          id: itemId,
        },
      ]);
    },
    [locallyCleared, props.formContext]
  );

  const getAvailableOptions = useCallback(
    (currentPsalmId: string | undefined, allSelectedPsalmIds: string[]) => {
      const allOptions =
        psalmerAll?.db?.BOPsalm_All?.map(item => ({
          name: item.NRTitel,
          value: item.id,
          SortNummer: item.SortNummer,
        }))
          .filter(obj => obj.name && obj.value)
          .filter(
            (obj, index, self) =>
              index === self.findIndex(t => t.value === obj.value)
          ) || [];

      // Exclude all selected psalms except the one belonging to this row,
      // so options are filtered based on what is currently rendered — not
      // stale form context — preventing the brief visual duplicate on selection.
      const otherSelectedIds = allSelectedPsalmIds.filter(
        id => id !== currentPsalmId
      );

      return allOptions
        .filter(option => !otherSelectedIds.includes(option.value))
        .sort((a: any, b: any) => a?.SortNummer - b?.SortNummer);
    },
    [psalmerAll]
  );

  // Pad up to the soft limit for new activities, then allow extra rows up to the hard limit
  const rowsFromSoftLimit =
    aktPsalmerAllFormContext.length < softLimit
      ? aktPsalmerAllFormContext.concat(
          repeat(undefined, softLimit - aktPsalmerAllFormContext.length)
        )
      : aktPsalmerAllFormContext;

  // Total displayed rows = rows from context (or soft limit) + any user-added extra rows,
  // capped at the hard limit
  const totalRowsToShow = Math.min(
    rowsFromSoftLimit.length + extraRows,
    HARD_LIMIT
  );

  const checkNumberOfPsalmerToAdd =
    rowsFromSoftLimit.length < totalRowsToShow
      ? rowsFromSoftLimit.concat(
          repeat(undefined, totalRowsToShow - rowsFromSoftLimit.length)
        )
      : rowsFromSoftLimit;

  const canAddMoreRows =
    !props.isNotWithinValidDateRange &&
    checkNumberOfPsalmerToAdd.length < HARD_LIMIT;

  const handleAddRow = () => {
    setExtraRows(prev => prev + 1);
  };

  // Collect all psalm IDs currently shown in the rendered rows (not from stale
  // form context). This is the source of truth for duplicate-filtering so that
  // a value selected mid-edit is immediately excluded from all other rows.
  const allSelectedPsalmIds: string[] = checkNumberOfPsalmerToAdd
    .map((item: any) => item?._Associations?.Psalm?.id)
    .filter(Boolean);

  return (
    <EDBox
      sx={{
        display: 'flex',
        flexDirection: 'column',
        padding: 2,
        minWidth:
          isLargeScreen && routineIDBenamning === 'Kalender' ? '500px' : '0px',
      }}
      testID="Psalmer"
    >
      <EDTypography bold color="textSecondary" variant="overline">
        Psalmer
      </EDTypography>

      {checkNumberOfPsalmerToAdd?.map(
        (
          item:
            | StatistikrapporteringQuery_node_TEDBOAktivitet__Associations_AktPsalmer_All
            | undefined,
          index: number
        ) => {
          // A saved row that was cleared is kept in locallyCleared until blur.
          // Show it as empty so the UI reflects the user's action immediately.
          const isLocallyCleared = item?.id
            ? locallyCleared[item.id] ?? false
            : false;
          const value =
            !isLocallyCleared && item?._Associations?.Psalm
              ? {
                  name: item?._Associations?.Psalm?.NRTitel,
                  value: item?._Associations?.Psalm?.id,
                  SortNummer: item._Associations?.Psalm?.SortNummer,
                }
              : null;
          return (
            <EDBox
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
              }}
              key={item?.id ?? `empty-row-${index}`}
              paddingRight={0.5}
            >
              <EDAutoCompleteEdit<any, undefined, undefined, undefined>
                fullWidth
                value={value}
                // When value is null the row is empty. Explicitly controlling
                // inputValue to '' prevents MUI's uncontrolled input from
                // retaining whatever the user typed after the selection was
                // committed to a different (earlier) row.
                inputValue={item?.id && isLocallyCleared ? '' : undefined}
                options={getAvailableOptions(value?.value, allSelectedPsalmIds)}
                onChange={option =>
                  updateValue(item?.id, option, index >= softLimit)
                }
                onBlur={
                  isLocallyCleared && item?.id
                    ? () => commitDeferredDelete(item.id!)
                    : undefined
                }
                getOptionLabel={option => option?.name ?? ''}
                getOptionSelected={(option: any, value: any) =>
                  option.value === value.value
                }
                popperMaxHeight={'100px'}
                disabled={props.isNotWithinValidDateRange}
                testId="psalmer-input-field"
              />
              <EDBox
                sx={{
                  display: 'flex',
                  paddingTop: 1,
                }}
              ></EDBox>
            </EDBox>
          );
        }
      )}

      {canAddMoreRows && (
        <EDButton
          marginLeft={-1}
          disabled={props.isNotWithinValidDateRange}
          onClick={handleAddRow}
          label="LÄGG TILL PSALM"
          flat
          tooltip={'Lägger till en psalm upp till tio psalmer möjliga.'}
          icon={
            <EDIcon
              iconType="add"
              color={props.isNotWithinValidDateRange ? 'disabled' : 'primary'}
            ></EDIcon>
          }
        />
      )}
    </EDBox>
  );
};

export default Psalmer;
